<template>
  <!--
    An entry with `href` leaves this app: a real anchor and a real browser
    navigation, because a router-link would be caught by this router, matched
    by its catch-all, and redirected straight back to /dispatch.

    No nav entry takes this branch today — the live map used to, while it was a
    separate app at /tracking/, and it is an ordinary route now. The branch
    stays because it is generic: it is what the nav data shape already says an
    entry without a `to` means, and it is the only correct rendering of one.

    It carries no active treatment and never can. Whatever an `href` points at
    is not this app, so an `aria-current` or an amber edge on such an entry
    would be a state this component is in no position to know. Faking it from
    `window.location` would make the entry look active for the one instant
    before the page is replaced and never again.
  -->
  <a v-if="link.href" class="lw-p-nav__link" :href="link.href">
    <i v-if="link.fa" class="fa" :class="link.fa" aria-hidden="true" />
    <span class="lw-p-nav__link-name">{{ link.name }}</span>
  </a>

  <!--
    The scoped-slot form of <router-link> (`custom` + `v-slot`, which is how
    vue-router 4 spells it) rather than the plain element form, for one reason: the plain form gives no way to set an
    attribute that depends on whether the link is active, and the active nav
    entry needs `aria-current="page"`.

    Today "you are here" is an amber left edge and a brighter white, which is
    colour and position and nothing else. The plan's doctrine is that a state
    may never be carried by colour alone (refresh_plan.md section 1), and the
    audit counted zero `aria-current` in 89 files (audit_web.md 6.2, S5). A
    dispatcher using a screen reader currently has no way to learn which of
    the twelve nav entries they are on.

    `active-class` is still handed to the slot's own `isActive`, and the class
    is applied by hand below, so `.router-link-active` keeps appearing on the
    element exactly as it did before. Nothing that reads that class breaks.

    `isActive` rather than `isExactActive`: /loads/123 should light Dispatch,
    which is what the prefix match gives, and it is the behaviour the element
    form had before this change.
  -->
  <router-link v-else v-slot="{ href, navigate, isActive }" :to="link.to" custom>
    <a
      class="lw-p-nav__link"
      :class="{ 'router-link-active': isActive }"
      :href="href"
      :aria-current="isActive ? 'page' : null"
      @click="navigate"
    >
      <i v-if="link.fa" class="fa" :class="link.fa" aria-hidden="true" />
      <span class="lw-p-nav__link-name">{{ link.name }}</span>
    </a>
  </router-link>
</template>

<script>
export default {
  props: {
    link: {
      required: true,
      type: Object,
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../../sass/mixins.generated';

/*
  Design refresh phase 2, surface 4 (the shell).

  Every rule that paints a nav link now lives HERE, in the component that
  renders it, and is scoped. They used to be split between this file's global
  `#sidebar a` block and nav.vue's global block, which is why nav.vue could
  reach `.nav-link` at all. Once both are scoped that stops working: a
  parent's scoped CSS reaches a child component's ROOT element and nothing
  deeper, and navLink is rendered both as nav.vue's grandchild (through
  navGroup) and as its child. Rules that follow the element they style are the
  only arrangement that survives scoping.

  Every class below is renamed into the lw-p- page namespace: `.nav-link` is
  now `.lw-p-nav__link`, `.nav-link-name` is `.lw-p-nav__link-name`. The one
  selector that keeps its old name on purpose is `.router-link-active` — it is
  vue-router's own vocabulary, not a class this file invented, and the plan
  for this surface is explicit that it has to keep appearing exactly as it
  did before now that this component applies it by hand instead of the router
  doing it automatically.
*/
.lw-p-nav__link {
  color: var(--lw-fg-inverse);
  text-decoration: none;
  width: 100%;
  display: block;
  padding: var(--lw-space-2) var(--lw-space-6);
  border-left: 3px solid transparent;
  font: var(--lw-type-label);
  letter-spacing: var(--lw-type-label-tracking);
  text-transform: var(--lw-type-label-transform);
  /*
    SIGNALWORKS' `.chassis nav a` is `rgba(255, 255, 255, .62)`
    (prototypes/direction-1.html:157). Over a solid ground, white at 62%
    opacity is the same pixel as that rgba, and it is a token read rather
    than a fourth literal white — which is also what lets this file satisfy
    the colour gate, since the gate forbids the functional notations for
    exactly the reason they hide a hardcoded colour.
  */
  opacity: 0.62;
  transition: opacity var(--lw-motion-fast) var(--lw-motion-ease);

  @include lw-focus-visible;

  /*
    The ring is pulled INSIDE the link. These are full-bleed blocks in a rail
    with no horizontal room, so a ring at the token's positive offset is
    clipped on both sides by the sidebar and reads as two short vertical
    dashes. LwTable.vue:749 does the same thing to its rows for the same
    reason.
  */
  &:focus-visible {
    opacity: 1;
    outline-offset: calc(var(--lw-focus-ring-offset) * -1);
  }
}

.lw-p-nav__link:hover {
  background-color: var(--lw-navy-950);
  opacity: 1;
}

/*
  The active entry: full-strength ink and the amber edge. Amber is rationed to
  roughly one element per screen in this direction, and on the shell this is
  the one — which is why the sidebar gets no amber rule of its own and the
  avatar no longer gets a colour at all.
*/
.lw-p-nav__link.router-link-active {
  border-left-color: var(--lw-line-chassis);
  background-color: var(--lw-navy-800);
  opacity: 1;
}

/*
  Collapsed, the rail shows icons only: the label is hidden and the remaining
  glyph is centred. `.lw-p-nav__root-link` is applied by nav.vue to the
  component tag, so it lands on this component's root element — which is why
  only top-level entries lose their label and a group's children keep theirs.
*/
#sidebar.collapsed .lw-p-nav__link {
  text-align: center;
}

#sidebar.collapsed .lw-p-nav__root-link .lw-p-nav__link-name {
  display: none;
}

#sidebar.expanded .lw-p-nav__link .fa {
  padding-right: 0.3em;
}
</style>
